import { computed, ref, watch } from 'vue'
import { defineStore } from 'pinia'
import { hidManager } from '@/api/device/hid'
import {
  buildEqCurveCommands,
  buildEqLoginStatusCommand,
  buildEqModeCommand,
  buildEqSinglePointCommand,
  type EqDataPoint,
} from '@/api/device/commands'
import type { EqMode, EqPresetData } from '@/api/eq'
import {
  addEqPreset,
  getEqList,
  pinEqPreset,
  removeEqPreset,
  renameEqPreset,
  resetEqPreset,
  setLastEqPreset,
  updateEqPoint,
} from '@/api/eq'
import { useUserStore } from './user'
import { useDeviceStore } from './device'

export const EQ_DISPLAY_FREQUENCIES = [
  '20Hz', '60Hz', '120Hz', '250Hz', '500Hz', '800Hz', '1K',
  '2K', '4K', '6K', '8K', '10K', '12K', '20K',
] as const

const PROTOCOL_FREQUENCIES = [
  '25HZ', '50HZ', '75HZ', '150HZ', '300HZ', '600HZ', '1K',
  '2K', '3K', '6K', '8K', '10K', '12K', '16K',
]
const DEFAULT_Q = '0.75'
const MAX_CUSTOM_PRESETS = 20
const MODE_TO_HARDWARE: Record<EqMode, number> = { game: 0, movie: 1, music: 2 }
const HARDWARE_TO_MODE: EqMode[] = ['game', 'movie', 'music']

function makePreset(id: string, name: string, gains: number[], qValues: number[]): EqPresetData {
  return {
    id,
    name,
    datapoint: PROTOCOL_FREQUENCIES.map((freq, index) => ({
      fid: index + 1,
      freq,
      gain: gains[index] ?? 0,
      q: String(qValues[index] ?? DEFAULT_Q),
    })),
  }
}

// 内置值与 oldcode/js/eq_setting.js 完全一致。
const SYSTEM_PRESETS: Record<EqMode, EqPresetData[]> = {
  game: [
    makePreset('TacticalFPS', '战术竞技射击', [-5, -3, -1, 0, -4, 2, 6, 5, 4, 3, 2, 1, 0, 0], [1, 1.2, 1.4, .6, 1.8, 1.5, 1.8, 2, 2.5, 2, 1.5, 1.2, 1, .75]),
    makePreset('BattleRoyale', '大逃杀', [3.5, 4, 2, 3, -3.5, -1.5, 3, 4, 2, 4, 3, 2, 0, 0], [1.2, 1.4, 1.6, .7, 1.5, 1.2, 1.5, 1.8, 2, 2.2, 2, 1.5, 1, .75]),
    makePreset('MilitaryShooter', '军事射击', [3, 3, 1, 5, -4, 0, 2, 4, 3, 5, 4, 2, 0, -1], [1, 1.2, 1.4, .6, 1.8, 1.5, 1.8, 2, 2.5, 2, 1.5, 1.2, 1, .75]),
    makePreset('ImmersiveRPG', '沉浸式角色扮演', [3, 4, 3, 4, -1.5, 0, 2, 1, -1, 1, 3, 3, 2, 0], [1, 1.2, 1.4, .7, 1.5, 1.2, 1.5, 1.8, 2, 2, 1.5, 1.2, 1, .75]),
  ],
  music: [
    makePreset('Pop Mode', '流行歌曲', [4, 2, 0, 3, -4, -1, 4, 3, -1, 2, 1, 4, 1, 0], [1, 1.2, 1.4, .5, 1.8, 1.2, 1.5, 2, 2.5, 1.8, 1.5, 1.2, 1, .75]),
    makePreset('Bass+', '低音加强', [3.5, 6, 4, 5, -5, -3, 0, 1, -2, 0, 0, 3, 0, 0], [1.2, 1.4, 1.6, .6, 1.8, 1.5, 1.2, 1.5, 2, 1.5, 1.2, 1, 1, .75]),
    makePreset('Vocal Focus', '清爽人声', [-3, -2, -1, 1, -4, 0, 5, 6, 4, 2, 1, 3, 1, 0], [1, 1.2, 1.4, .5, 1.8, 1.5, 1.8, 2.2, 2.5, 1.8, 1.5, 1.5, 1, .75]),
    makePreset('DJ Mode', 'DJ专用', [3, 6, 3, 2, -5, -1, 1, 2, -1, 4, 3, 5, 2, 0], [1, 1.4, 1.6, .6, 1.8, 1.5, 1.2, 1.5, 2, 2, 1.8, 1.2, 1, .75]),
  ],
  movie: [
    makePreset('Theater Surround', '影视音效', [4, 5, 4, 3, -4, -1, 4, 3, 1, 2, 3, 4, 2, 0], [1.2, 1.5, 1.6, .6, 1.8, 1.2, 1.5, 1.8, 2, 1.8, 1.5, 1.2, 1, .75]),
    makePreset('Horror Atmosphere', '恐怖氛围', [3, 3, 0, -2, -4, -1, 1, 3, 2, 5, 6, 4, 3, 1], [1, 1.4, 1.5, .8, 1.8, 1.5, 1.5, 2.2, 2.5, 2, 2.5, 1.5, 1.2, .75]),
    makePreset('Sci-Fi Space', '科幻空间', [4, 5, 3, 2, -4, -3, 1, 2, 3, 4, 5, 5, 3, 1], [1.2, 1.5, 1.6, .6, 1.8, 1.5, 1.5, 1.8, 2, 2, 2.2, 1.5, 1.2, .75]),
  ],
}

const SYSTEM_PRESET_IDS: Record<EqMode, string[]> = {
  game: SYSTEM_PRESETS.game.map(item => item.id),
  music: SYSTEM_PRESETS.music.map(item => item.id),
  movie: SYSTEM_PRESETS.movie.map(item => item.id),
}

function clonePoints(points: EqDataPoint[]): EqDataPoint[] {
  return points.map(point => ({ ...point, gain: Number(point.gain) || 0, q: String(point.q || DEFAULT_Q) }))
}

function clonePresets(presets: EqPresetData[]): EqPresetData[] {
  return presets.map(preset => ({ ...preset, datapoint: clonePoints(preset.datapoint) }))
}

function createZeroCurve(template?: EqDataPoint[]): EqDataPoint[] {
  return PROTOCOL_FREQUENCIES.map((freq, index) => ({
    fid: index + 1,
    freq: template?.[index]?.freq || freq,
    gain: 0,
    q: template?.[index]?.q || DEFAULT_Q,
  }))
}

interface EqModeState {
  enabled: boolean
  currentPreset: string
  systemPresets: EqPresetData[]
  customPresets: EqPresetData[]
  lastPreset: string | null
  pinnedPreset: string | null
}

function createModeState(mode: EqMode): EqModeState {
  return {
    enabled: false,
    currentPreset: SYSTEM_PRESETS[mode][0].id,
    systemPresets: clonePresets(SYSTEM_PRESETS[mode]),
    customPresets: [],
    lastPreset: null,
    pinnedPreset: null,
  }
}

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

function showGlobalLightTip(message: string): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent<string>('app-light-tip', { detail: message }))
}

export const useEqStore = defineStore('eq', () => {
  const userStore = useUserStore()
  const deviceStore = useDeviceStore()
  userStore.loadFromStorage()
  const gameEq = ref<EqModeState>(createModeState('game'))
  const musicEq = ref<EqModeState>(createModeState('music'))
  const movieEq = ref<EqModeState>(createModeState('movie'))
  const activeMode = ref<EqMode>('game')
  const appliedMode = ref<EqMode | null>(null)
  const appliedPresetId = ref<string | null>(null)
  const remoteLoaded = ref<Record<EqMode, boolean>>({ game: false, music: false, movie: false })

  // -------------------- EQ 数据来源仲裁 --------------------
  // 登录态：标签 / 14 段曲线 / lastPreset / 置顶全部以服务器为最终来源；
  // 0x80 只允许在当前登录+连接上下文第一次回读时提供 EQ 开关。
  // 未登录：0x80 持续作为 eqEnabled / eqMode / eqTag 的实时来源。
  //
  // 为了消除 add/delete/pin/saveAs 后服务器 list 的短暂最终一致性延迟，
  // 这里只缓存“服务器已经确认成功”的快照，而不是把任意本地 UI 状态当成云端真值。
  // 刷新时先显示该服务器确认快照，同时立即重新拉服务器；最终仍由服务器校正。
  const sourceGeneration = ref(0)
  const mutationVersion = ref<Record<EqMode, number>>({ game: 0, music: 0, movie: 0 })
  const selectionVersion = ref<Record<EqMode, number>>({ game: 0, music: 0, movie: 0 })
  const cloudGuardUntil = ref<Record<EqMode, number>>({ game: 0, music: 0, movie: 0 })
  const reconcileTimers: Partial<Record<EqMode, ReturnType<typeof setTimeout>>> = {}
  const CLOUD_GUARD_MS = 6000
  const CLOUD_CACHE_PREFIX = 'eqCloudSnapshot:v2:'
  // 删除接口是最终一致的：接口成功后 /list 可能短时间仍返回已删除标签。
  // 用“待确认删除 tombstone”只屏蔽用户明确删除过的 presetId，直到服务器 list 真正确认它消失。
  // 这不是新的数据源，只是对服务器删除最终一致性的短期保护，并按账号 + linktype 隔离。
  const PENDING_DELETE_PREFIX = 'eqPendingDeletes:v1:'
  const PENDING_DELETE_TTL_MS = 120_000
  const pendingDeleteIds = ref<Record<EqMode, Record<string, number>>>({ game: {}, music: {}, movie: {} })

  // 新增接口同样存在“写接口已经发出，但 list 短时间仍是旧列表”的窗口。
  // 如果这时刷新页面，旧 list 会把刚新增的标签暂时隐藏，用户再次点 + 就可能生成同名标签。
  // pending add 只保存“已经退出编辑并开始提交”的新增操作，按账号 + linktype 隔离；
  // list 真正确认该 presetId 后立即清除，不把它当成新的长期数据源。
  const PENDING_ADD_PREFIX = 'eqPendingAdds:v1:'
  const PENDING_ADD_TTL_MS = 120_000
  type PendingAddEntry = { expiresAt: number; preset: EqPresetData }
  const pendingAdds = ref<Record<EqMode, Record<string, PendingAddEntry>>>({ game: {}, music: {}, movie: {} })
  let loggedInHardwareEnabledCaptured = false
  let sourceContextKey = ''
  const apiDebounceTimers = ref<Record<string, ReturnType<typeof setTimeout>>>({})
  const lastPresetTimers = ref<Record<string, ReturnType<typeof setTimeout>>>({})
  let deviceQueue: Promise<void> = Promise.resolve()

  // “+” 新增出来的标签先作为本地草稿存在：只有退出行内编辑时才真正调用新增接口。
  // 这样点击 + 后 UI 可以立即出现，不需要等待网络；同时避免“先 add 默认名、再 rename”造成接口竞态。
  const draftPresetIds = new Set<string>()

  // 用户从 UI 主动切换 EQ 后，0x80 可能恰好回到“切换前”的旧状态。
  // 在硬件确认本次写入前短暂忽略旧回读，避免底部菜单/按钮先变新值、又闪回旧值。
  let pendingHardwareWrite: {
    enabled: boolean
    mode: EqMode
    presetId: string | null
    expiresAt: number
  } | null = null
  const EQ_WRITE_GUARD_MS = 5000

  function markPendingHardwareWrite(enabled: boolean, mode: EqMode, presetId: string | null): void {
    pendingHardwareWrite = { enabled, mode, presetId, expiresAt: Date.now() + EQ_WRITE_GUARD_MS }
  }

  // activeMode 表示用户当前正在浏览的页签，appliedMode 才表示硬件实际应用的模式。
  // 0x80 每 2 秒都会回读一次，因此只允许首次硬件同步初始化浏览页签，
  // 后续回读不能把用户刚切换到的 Music/Movie 强制拉回硬件当前模式。
  let hardwareStatusInitialized = false

  function getModeState(mode: EqMode): EqModeState {
    if (mode === 'music') return musicEq.value
    if (mode === 'movie') return movieEq.value
    return gameEq.value
  }

  function presetsForMode(mode: EqMode): EqPresetData[] {
    const state = getModeState(mode)
    return [...state.systemPresets, ...state.customPresets]
  }

  function findCustomPresetLocation(presetId: string): { mode: EqMode; state: EqModeState; preset: EqPresetData } | null {
    for (const mode of ['game', 'music', 'movie'] as EqMode[]) {
      const state = getModeState(mode)
      const preset = state.customPresets.find(item => item.id === presetId)
      if (preset) return { mode, state, preset }
    }
    return null
  }

  function isDraftPreset(presetId: string): boolean {
    return draftPresetIds.has(presetId)
  }

  function markMutation(mode: EqMode): void {
    mutationVersion.value[mode] += 1
  }

  function markSelection(mode: EqMode): void {
    selectionVersion.value[mode] += 1
  }

  function clearReconcileTimer(mode: EqMode): void {
    const timer = reconcileTimers[mode]
    if (!timer) return
    clearTimeout(timer)
    delete reconcileTimers[mode]
  }

  function currentCloudCacheKey(): string | null {
    if (!isLoggedIn()) return null
    return `${CLOUD_CACHE_PREFIX}${encodeURIComponent(getUuid())}:${getLinktype()}`
  }

  function currentPendingDeleteKey(): string | null {
    if (!isLoggedIn()) return null
    return `${PENDING_DELETE_PREFIX}${encodeURIComponent(getUuid())}:${getLinktype()}`
  }

  function currentPendingAddKey(): string | null {
    if (!isLoggedIn()) return null
    return `${PENDING_ADD_PREFIX}${encodeURIComponent(getUuid())}:${getLinktype()}`
  }

  function prunePendingDeletes(mode?: EqMode): void {
    const now = Date.now()
    const modes = mode ? [mode] : (['game', 'music', 'movie'] as EqMode[])
    for (const currentMode of modes) {
      const entries = pendingDeleteIds.value[currentMode]
      Object.keys(entries).forEach(id => {
        if (!Number.isFinite(entries[id]) || entries[id] <= now) delete entries[id]
      })
    }
  }

  function savePendingDeletes(): void {
    const key = currentPendingDeleteKey()
    if (!key) return
    prunePendingDeletes()
    try {
      const hasAny = (['game', 'music', 'movie'] as EqMode[]).some(mode => Object.keys(pendingDeleteIds.value[mode]).length)
      if (!hasAny) {
        localStorage.removeItem(key)
        return
      }
      localStorage.setItem(key, JSON.stringify({ version: 1, modes: pendingDeleteIds.value }))
    } catch (error) {
      console.warn('[EQ] 保存待确认删除状态失败:', error)
    }
  }

  function loadPendingDeletes(): void {
    pendingDeleteIds.value = { game: {}, music: {}, movie: {} }
    const key = currentPendingDeleteKey()
    if (!key) return
    try {
      const raw = localStorage.getItem(key)
      if (!raw) return
      const parsed = JSON.parse(raw)
      if (parsed?.version !== 1 || !parsed.modes) return
      for (const mode of ['game', 'music', 'movie'] as EqMode[]) {
        const source = parsed.modes[mode]
        if (source && typeof source === 'object') pendingDeleteIds.value[mode] = { ...source }
      }
      prunePendingDeletes()
      savePendingDeletes()
    } catch (error) {
      console.warn('[EQ] 读取待确认删除状态失败:', error)
    }
  }

  function addPendingDelete(mode: EqMode, presetId: string): void {
    pendingDeleteIds.value[mode][presetId] = Date.now() + PENDING_DELETE_TTL_MS
    savePendingDeletes()
  }

  function clearPendingDelete(mode: EqMode, presetId: string): void {
    if (!(presetId in pendingDeleteIds.value[mode])) return
    delete pendingDeleteIds.value[mode][presetId]
    savePendingDeletes()
  }

  function activePendingDeleteIds(mode: EqMode): Set<string> {
    prunePendingDeletes(mode)
    return new Set(Object.keys(pendingDeleteIds.value[mode]))
  }

  function prunePendingAdds(mode?: EqMode): void {
    const now = Date.now()
    const modes = mode ? [mode] : (['game', 'music', 'movie'] as EqMode[])
    for (const currentMode of modes) {
      const entries = pendingAdds.value[currentMode]
      Object.keys(entries).forEach(id => {
        const entry = entries[id]
        if (!entry || !Number.isFinite(entry.expiresAt) || entry.expiresAt <= now) delete entries[id]
      })
    }
  }

  function savePendingAdds(): void {
    const key = currentPendingAddKey()
    if (!key) return
    prunePendingAdds()
    try {
      const hasAny = (['game', 'music', 'movie'] as EqMode[]).some(mode => Object.keys(pendingAdds.value[mode]).length)
      if (!hasAny) {
        localStorage.removeItem(key)
        return
      }
      localStorage.setItem(key, JSON.stringify({ version: 1, modes: pendingAdds.value }))
    } catch (error) {
      console.warn('[EQ] 保存待确认新增状态失败:', error)
    }
  }

  function loadPendingAdds(): void {
    pendingAdds.value = { game: {}, music: {}, movie: {} }
    const key = currentPendingAddKey()
    if (!key) return
    try {
      const raw = localStorage.getItem(key)
      if (!raw) return
      const parsed = JSON.parse(raw)
      if (parsed?.version !== 1 || !parsed.modes) return
      for (const mode of ['game', 'music', 'movie'] as EqMode[]) {
        const source = parsed.modes[mode]
        if (!source || typeof source !== 'object') continue
        const next: Record<string, PendingAddEntry> = {}
        Object.entries(source as Record<string, PendingAddEntry>).forEach(([id, entry]) => {
          if (!entry?.preset) return
          next[id] = {
            expiresAt: Number(entry.expiresAt) || 0,
            preset: { ...normalizePreset(entry.preset), isCustom: true },
          }
        })
        pendingAdds.value[mode] = next
      }
      prunePendingAdds()
      savePendingAdds()
    } catch (error) {
      console.warn('[EQ] 读取待确认新增状态失败:', error)
    }
  }

  function addPendingAdd(mode: EqMode, preset: EqPresetData): void {
    pendingAdds.value[mode][preset.id] = {
      expiresAt: Date.now() + PENDING_ADD_TTL_MS,
      preset: { ...normalizePreset(preset), isCustom: true },
    }
    savePendingAdds()
  }

  function clearPendingAdd(mode: EqMode, presetId: string): void {
    if (!(presetId in pendingAdds.value[mode])) return
    delete pendingAdds.value[mode][presetId]
    savePendingAdds()
  }

  function activePendingAdds(mode: EqMode): PendingAddEntry[] {
    prunePendingAdds(mode)
    return Object.values(pendingAdds.value[mode])
  }

  function hydratePendingAddsIntoState(): void {
    for (const mode of ['game', 'music', 'movie'] as EqMode[]) {
      const entries = activePendingAdds(mode)
      if (!entries.length) continue
      const state = getModeState(mode)
      const existingIds = new Set(state.customPresets.map(item => item.id))
      const sorted = entries.slice().sort((a, b) => Number(a.preset.createdAt || 0) - Number(b.preset.createdAt || 0))
      sorted.forEach(entry => {
        if (!existingIds.has(entry.preset.id)) {
          state.customPresets.push({ ...normalizePreset(entry.preset), isCustom: true })
          existingIds.add(entry.preset.id)
        }
      })
      const latest = sorted[sorted.length - 1]?.preset
      if (latest && existingIds.has(latest.id)) state.currentPreset = latest.id
    }
  }

  function resumePendingDeletes(): void {
    if (!isLoggedIn()) return
    const uuid = getUuid()
    const linktype = getLinktype()
    for (const mode of ['game', 'music', 'movie'] as EqMode[]) {
      for (const presetId of activePendingDeleteIds(mode)) {
        // 页面刷新可能中断上一条 fetch。重新发送删除是幂等兜底；
        // tombstone 是否清除仍只由后续 list “确认该 id 已不存在”来决定。
        void removeEqPreset(mode, uuid, presetId, linktype)
          .then(() => scheduleRemoteReconcile(mode, 350))
          .catch(error => console.warn('[EQ] 恢复待确认删除请求失败:', mode, presetId, error))
      }
    }
  }

  function modeSnapshot(mode: EqMode) {
    const state = getModeState(mode)
    const safeCurrent = draftPresetIds.has(state.currentPreset)
      ? (state.lastPreset && !draftPresetIds.has(state.lastPreset) ? state.lastPreset : state.systemPresets[0]?.id || '')
      : state.currentPreset
    return {
      systemPresets: clonePresets(state.systemPresets),
      customPresets: clonePresets(state.customPresets.filter(preset => !draftPresetIds.has(preset.id))),
      currentPreset: safeCurrent,
      lastPreset: state.lastPreset && !draftPresetIds.has(state.lastPreset) ? state.lastPreset : null,
      pinnedPreset: state.pinnedPreset && !draftPresetIds.has(state.pinnedPreset) ? state.pinnedPreset : null,
    }
  }

  function readCloudSnapshot(): any | null {
    const key = currentCloudCacheKey()
    if (!key) return null
    try {
      const raw = localStorage.getItem(key)
      if (!raw) return null
      const parsed = JSON.parse(raw)
      return parsed && parsed.version === 2 ? parsed : null
    } catch (error) {
      console.warn('[EQ] 读取服务器快照失败:', error)
      return null
    }
  }

  function writeCloudModeSnapshot(
    mode: EqMode,
    options: { protect?: boolean; confirmActiveMode?: boolean } = {},
  ): void {
    const key = currentCloudCacheKey()
    if (!key) return
    try {
      const existing = readCloudSnapshot() || { version: 2, modes: {}, guardUntil: {} }
      if (options.protect) cloudGuardUntil.value[mode] = Date.now() + CLOUD_GUARD_MS
      existing.version = 2
      existing.updatedAt = Date.now()
      existing.modes = existing.modes || {}
      existing.modes[mode] = modeSnapshot(mode)
      existing.guardUntil = existing.guardUntil || {}
      existing.guardUntil[mode] = cloudGuardUntil.value[mode] || 0
      if (options.confirmActiveMode || !existing.activeMode) existing.activeMode = activeMode.value
      localStorage.setItem(key, JSON.stringify(existing))
    } catch (error) {
      console.warn('[EQ] 保存服务器快照失败:', error)
    }
  }

  function hydrateCloudSnapshot(): boolean {
    if (!isLoggedIn()) return false
    const snapshot = readCloudSnapshot()
    if (!snapshot) return false

    if (['game', 'music', 'movie'].includes(snapshot.activeMode)) {
      activeMode.value = snapshot.activeMode
    }

    for (const mode of ['game', 'music', 'movie'] as EqMode[]) {
      const source = snapshot.modes?.[mode]
      if (!source) continue
      const state = getModeState(mode)
      if (Array.isArray(source.systemPresets) && source.systemPresets.length) {
        state.systemPresets = source.systemPresets.map(normalizePreset)
      }
      if (Array.isArray(source.customPresets)) {
        const pendingDeletes = activePendingDeleteIds(mode)
        state.customPresets = source.customPresets
          .filter((preset: EqPresetData) => !pendingDeletes.has(preset.id))
          .map((preset: EqPresetData, index: number) => ({
            ...normalizePreset(preset),
            isCustom: true,
            createdAt: preset.createdAt || Date.now() + index,
          }))
      }
      state.lastPreset = source.lastPreset || null
      state.pinnedPreset = source.pinnedPreset || null
      const candidate = source.currentPreset || source.lastPreset
      if (candidate && presetsForMode(mode).some(preset => preset.id === candidate)) {
        state.currentPreset = candidate
      }
      const guard = Number(snapshot.guardUntil?.[mode] || 0)
      cloudGuardUntil.value[mode] = Number.isFinite(guard) ? guard : 0
    }
    return true
  }

  function cloudModeFingerprint(mode: EqMode): string | null {
    const snapshot = readCloudSnapshot()
    const source = snapshot?.modes?.[mode]
    if (!source) return null
    const compact = {
      system: (source.systemPresets || []).map((preset: EqPresetData) => ({
        id: preset.id,
        name: preset.name,
        datapoint: preset.datapoint?.map(point => [point.fid, point.freq, Number(point.gain) || 0, String(point.q || DEFAULT_Q)]),
      })),
      custom: (source.customPresets || []).map((preset: EqPresetData) => ({
        id: preset.id,
        name: preset.name,
        datapoint: preset.datapoint?.map(point => [point.fid, point.freq, Number(point.gain) || 0, String(point.q || DEFAULT_Q)]),
      })),
      lastPreset: source.lastPreset || null,
      defaultPreset: source.pinnedPreset || null,
    }
    return JSON.stringify(compact)
  }

  function remoteFingerprint(
    systemPresets: EqPresetData[],
    customPresets: EqPresetData[],
    lastPreset: string | null,
    pinnedPreset: string | null,
  ): string {
    return JSON.stringify({
      system: systemPresets.map(preset => ({
        id: preset.id,
        name: preset.name,
        datapoint: preset.datapoint.map(point => [point.fid, point.freq, Number(point.gain) || 0, String(point.q || DEFAULT_Q)]),
      })),
      custom: customPresets.map(preset => ({
        id: preset.id,
        name: preset.name,
        datapoint: preset.datapoint.map(point => [point.fid, point.freq, Number(point.gain) || 0, String(point.q || DEFAULT_Q)]),
      })),
      lastPreset: lastPreset || null,
      defaultPreset: pinnedPreset || null,
    })
  }

  function scheduleRemoteReconcile(mode: EqMode, delayMs = 650): void {
    clearReconcileTimer(mode)
    reconcileTimers[mode] = setTimeout(() => {
      delete reconcileTimers[mode]
      void loadFromRemote(mode, true)
    }, delayMs)
  }

  const currentModeState = computed(() => getModeState(activeMode.value))
  const currentSettings = currentModeState
  const allPresets = computed(() => presetsForMode(activeMode.value))
  const currentPreset = computed(() => allPresets.value.find(preset => preset.id === currentModeState.value.currentPreset))
  const currentPresetData = computed(() => currentPreset.value?.datapoint || createZeroCurve())
  const isSystemPreset = computed(() => (presetId: string) => SYSTEM_PRESET_IDS[activeMode.value].includes(presetId))
  const canAddMoreCustom = computed(() => currentModeState.value.customPresets.length < MAX_CUSTOM_PRESETS)
  const isEnabled = computed(() => appliedMode.value !== null && !!getModeState(appliedMode.value).enabled)
  const currentMode = computed(() => activeMode.value)
  const slidersInteractive = computed(() => (
    isEnabled.value
    && appliedMode.value === activeMode.value
    && appliedPresetId.value === currentModeState.value.currentPreset
  ))

  function isLoggedIn(): boolean {
    return userStore.isLoggedIn && !!userStore.uuid
  }

  function getUuid(): string {
    return userStore.uuid || ''
  }

  function getLinktype(): string {
    const connection = deviceStore.connectionType || hidManager.getConnectionType()
    return connection === '2.4G' ? '0' : '1'
  }

  function normalizePreset(preset: EqPresetData): EqPresetData {
    return { ...preset, datapoint: clonePoints(preset.datapoint) }
  }

  function enqueueDevice(task: () => Promise<void>): Promise<void> {
    deviceQueue = deviceQueue.then(task).catch(error => {
      console.error('[EQ] 设备命令执行失败:', error)
    })
    return deviceQueue
  }

  async function sendLoginStatus(): Promise<void> {
    await hidManager.sendData(buildEqLoginStatusCommand(isLoggedIn()), false)
  }

  async function getPositioningProtocolState(): Promise<{ enabled: boolean; tag: number }> {
    try {
      const { useChijiStore } = await import('./chiji')
      const store = useChijiStore()
      const index = store.allPresets.findIndex(item => item.id === store.currentPresetId)
      return { enabled: store.enabled, tag: Math.max(0, index) }
    } catch {
      return { enabled: false, tag: 0 }
    }
  }

  async function sendModeState(enabled: boolean, mode: EqMode, presetId: string | null): Promise<void> {
    if (!hidManager.isConnected()) return
    const positioning = await getPositioningProtocolState()
    const tag = enabled && presetId
      ? Math.max(0, presetsForMode(mode).findIndex(preset => preset.id === presetId))
      : 0
    await sendLoginStatus()
    await hidManager.sendData(buildEqModeCommand(
      enabled,
      MODE_TO_HARDWARE[mode],
      tag,
      positioning.enabled,
      positioning.tag,
    ), false)
  }

  async function sendCurve(points: EqDataPoint[], includeLogin = true): Promise<void> {
    if (!hidManager.isConnected()) return
    if (includeLogin) await sendLoginStatus()
    const commands = buildEqCurveCommands(points)
    for (let index = 0; index < commands.length; index += 1) {
      await hidManager.sendData(commands[index], false)
      if (index < commands.length - 1) await delay(200)
    }
  }

  function setActiveMode(mode: EqMode) {
    activeMode.value = mode
  }

  const switchMode = setActiveMode

  async function applyPreset(
    mode: EqMode,
    presetId: string,
    options: { persist?: boolean; syncLastPreset?: boolean; markUserSelection?: boolean } = {},
  ): Promise<void> {
    const state = getModeState(mode)
    const preset = presetsForMode(mode).find(item => item.id === presetId)
    if (!preset) return
    const persist = options.persist !== false
    const syncLastPreset = options.syncLastPreset !== false
    const markUserSelection = options.markUserSelection !== false

    // 先一次性完成 UI 状态切换，再进行异步互斥/硬件下发。
    // 原先这里先 await 一键吃鸡状态，activeMode 已切换但目标 mode 仍是 disabled，
    // 会让底部 EQ 文案和下拉选中态出现一帧 OFF/未选中的闪烁。
    ;[gameEq.value, musicEq.value, movieEq.value].forEach(item => { item.enabled = false })
    activeMode.value = mode
    state.enabled = true
    state.currentPreset = presetId
    state.lastPreset = presetId
    appliedMode.value = mode
    appliedPresetId.value = presetId
    markPendingHardwareWrite(true, mode, presetId)
    if (markUserSelection) markSelection(mode)
    if (persist) saveToStorage()
    if (syncLastPreset) scheduleSetLastPreset(mode, presetId)

    try {
      const { useChijiStore } = await import('./chiji')
      const chijiStore = useChijiStore()
      if (chijiStore.enabled) {
        await chijiStore.setEnabled(false)
        showGlobalLightTip('One-click positioning has been turned off.')
      }
    } catch {
      // 状态仓库可以在测试中独立使用。
    }

    await enqueueDevice(async () => {
      await sendModeState(true, mode, presetId)
      if (hidManager.isConnected()) await delay(30)
      await sendCurve(preset.datapoint, false)
    })
  }

  async function setEqEnabled(enabled: boolean): Promise<void> {
    if (enabled) {
      await applyPreset(activeMode.value, currentModeState.value.currentPreset)
      return
    }

    const mode = appliedMode.value || activeMode.value
    ;[gameEq.value, musicEq.value, movieEq.value].forEach(item => { item.enabled = false })
    appliedMode.value = null
    appliedPresetId.value = null
    markPendingHardwareWrite(false, mode, null)
    saveToStorage()

    await enqueueDevice(async () => {
      await sendModeState(false, mode, null)
      if (hidManager.isConnected()) await delay(30)
      await sendCurve(createZeroCurve(), false)
    })
  }

  async function selectPreset(presetId: string): Promise<void> {
    await applyPreset(activeMode.value, presetId)
  }

  async function updateEqGain(bandIndex: number, gain: number, skipDeviceSend = false): Promise<void> {
    const preset = currentPreset.value
    const point = preset?.datapoint[bandIndex]
    if (!preset || !point || !slidersInteractive.value) return
    point.gain = Math.max(-12, Math.min(12, Math.round(gain * 2) / 2))
    if (isLoggedIn() && !isDraftPreset(preset.id)) markMutation(activeMode.value)

    if (!skipDeviceSend && hidManager.isConnected()) {
      const command = buildEqSinglePointCommand(bandIndex, point)
      void enqueueDevice(async () => {
        await sendLoginStatus()
        await hidManager.sendData(command, false)
      })
    }

    if (isLoggedIn() && !isDraftPreset(preset.id)) {
      debouncedSyncPointToApi(activeMode.value, preset.id, bandIndex, point)
    }
  }

  async function updateEqFrequency(bandIndex: number, frequency: string): Promise<void> {
    const preset = currentPreset.value
    const point = preset?.datapoint[bandIndex]
    if (!preset || !point || !slidersInteractive.value) return
    point.freq = frequency
    await updateEqGain(bandIndex, point.gain)
  }

  function debouncedSyncPointToApi(mode: EqMode, presetId: string, bandIndex: number, point: EqDataPoint) {
    const key = `${mode}-${presetId}-${bandIndex}`
    if (apiDebounceTimers.value[key]) clearTimeout(apiDebounceTimers.value[key])
    apiDebounceTimers.value[key] = setTimeout(async () => {
      const uuid = getUuid()
      if (!uuid) return
      try {
        const response = await updateEqPoint(mode, uuid, presetId, point, getLinktype())
        if (Number(response.code) === 1) {
          // 单点接口成功后，当前曲线已经是服务器确认的新值。
          // 更新服务器快照并短暂保护，避免紧接着的 list 仍返回旧曲线把它顶回去。
          writeCloudModeSnapshot(mode, { protect: true, confirmActiveMode: true })
          scheduleRemoteReconcile(mode)
        }
      } catch (error) {
        console.error('[EQ] 同步频点失败:', error)
      }
      delete apiDebounceTimers.value[key]
    }, 500)
  }

  function scheduleSetLastPreset(mode: EqMode, presetId: string): void {
    if (!isLoggedIn()) return
    const key = `${mode}-${presetId}`
    if (lastPresetTimers.value[key]) clearTimeout(lastPresetTimers.value[key])
    lastPresetTimers.value[key] = setTimeout(async () => {
      try {
        const response = await setLastEqPreset(mode, getUuid(), presetId, getLinktype())
        if (Number(response.code) === 1) {
          const state = getModeState(mode)
          state.lastPreset = presetId
          writeCloudModeSnapshot(mode, { protect: true, confirmActiveMode: true })
          scheduleRemoteReconcile(mode)
        }
      } catch (error) {
        console.error('[EQ] 保存上次预设失败:', error)
      }
      delete lastPresetTimers.value[key]
    }, 200)
  }

  async function applyCurrentEq(): Promise<void> {
    const preset = currentPreset.value
    if (!preset) return
    await enqueueDevice(() => sendCurve(preset.datapoint))
  }

  function createCustomPresetDraft(name: string, datapoint: EqDataPoint[]): string | null {
    // 锁定点击新增时所在模式；草稿立即进入 UI，不等待后端。
    const mode = activeMode.value
    const state = getModeState(mode)
    if (state.customPresets.length >= MAX_CUSTOM_PRESETS) return null

    const presetId = `custom-${mode}-${Date.now()}`
    const newPreset: EqPresetData = {
      id: presetId,
      name: name.trim(),
      datapoint: clonePoints(datapoint),
      isCustom: true,
      createdAt: Date.now(),
    }

    state.customPresets.push(newPreset)
    draftPresetIds.add(presetId)

    // applyPreset 在第一个 await 之前就会完成选中态更新，因此标签会在本次点击内立即变蓝。
    // 草稿阶段不写 localStorage / setLast，避免临时 id 被保存或发给服务器。
    void applyPreset(mode, presetId, { persist: false, syncLastPreset: false })
    return presetId
  }

  async function finalizeCustomPresetDraft(presetId: string, name: string): Promise<boolean> {
    const location = findCustomPresetLocation(presetId)
    if (!location || !draftPresetIds.has(presetId)) return false

    const { mode, state, preset } = location
    const normalizedName = name.trim().slice(0, 20)
    if (!normalizedName) return false
    if (presetsForMode(mode).some(item => item.id !== presetId && item.name.trim().toLowerCase() === normalizedName.toLowerCase())) {
      return false
    }

    preset.name = normalizedName
    const oldId = preset.id
    markMutation(mode)

    if (isLoggedIn()) {
      // 在真正发请求前先记录待确认新增。刷新/关闭页面时，即使 list 暂时还是旧数据，
      // 新标签也不会凭空消失，更不会因为名字又变成可用而再次创建同名标签。
      addPendingAdd(mode, preset)
      try {
        const response = await addEqPreset(mode, getUuid(), preset, getLinktype())
        // 与旧版接口语义保持一致：code === 1 就代表新增成功。
        // 部分环境的 addEqData 成功响应并不会返回 data.id；上一版把“没有 id”误判成失败，
        // 结果就是服务器已经创建成功、前端却连续弹 Error，并且再次操作时制造重复标签。
        if (Number(response.code) !== 1) {
          clearPendingAdd(mode, oldId)
          return false
        }

        const responseData = response.data as unknown
        let returnedId: string | null = null
        if (typeof responseData === 'string' || typeof responseData === 'number') {
          returnedId = String(responseData)
        } else if (responseData && typeof responseData === 'object') {
          const data = responseData as Record<string, unknown>
          const candidate = data.id ?? data.presetId ?? data.eqId
          if (typeof candidate === 'string' || typeof candidate === 'number') returnedId = String(candidate)
        }

        // 后端没有回传 id 时继续沿用提交给 addEqData 的客户端 id（旧版就是这个行为）。
        // draft 身份由 draftPresetIds 单独维护，因此 id 本身不需要承担“是否草稿”的语义。
        const remoteId = returnedId || oldId
        preset.id = remoteId
        draftPresetIds.delete(oldId)

        // 极少数后端会返回一个不同于提交值的 id：同步迁移 pending add，
        // 让刷新后的 list 仍按真正的服务器 id 做最终确认。
        if (remoteId !== oldId) {
          clearPendingAdd(mode, oldId)
          addPendingAdd(mode, preset)
        } else {
          addPendingAdd(mode, preset)
        }

        // 后端 id 返回后，把所有仍引用临时 id 的状态一次性替换掉，避免 UI/硬件状态断链。
        if (state.currentPreset === oldId) state.currentPreset = remoteId
        if (state.lastPreset === oldId) state.lastPreset = remoteId
        if (state.pinnedPreset === oldId) state.pinnedPreset = remoteId
        if (appliedMode.value === mode && appliedPresetId.value === oldId) appliedPresetId.value = remoteId
        if (pendingHardwareWrite?.mode === mode && pendingHardwareWrite.presetId === oldId) {
          pendingHardwareWrite.presetId = remoteId
        }

        saveToStorage()
        // addEqData 已经成功，当前新增标签就是服务器确认状态。
        writeCloudModeSnapshot(mode, { protect: true, confirmActiveMode: true })
        scheduleRemoteReconcile(mode)
        if (appliedMode.value === mode && appliedPresetId.value === remoteId) {
          scheduleSetLastPreset(mode, remoteId)
        }
        return true
      } catch (error) {
        // 正常在线情况下的明确网络异常需要撤销 pending；页面刷新/关闭导致 JS 上下文销毁时
        // 这段 catch 不会执行，pending 会保留下来并由下一页的服务器 list 继续确认。
        clearPendingAdd(mode, oldId)
        console.error('[EQ] 保存新增预设失败:', error)
        return false
      }
    }

    draftPresetIds.delete(oldId)
    saveToStorage()
    return true
  }

  async function addCustomPreset(name: string, datapoint: EqDataPoint[]): Promise<string | null> {
    // Save As / 对话框仍走“确认即保存”；内部复用同一套草稿保存逻辑，保证新增语义一致。
    if (currentModeState.value.customPresets.length >= MAX_CUSTOM_PRESETS) throw new Error('CUSTOM_PRESET_LIMIT')
    const presetId = createCustomPresetDraft(name, datapoint)
    if (!presetId) throw new Error('CUSTOM_PRESET_LIMIT')
    const success = await finalizeCustomPresetDraft(presetId, name)
    if (!success) {
      const location = findCustomPresetLocation(presetId)
      if (location) {
        const index = location.state.customPresets.findIndex(item => item.id === presetId)
        if (index >= 0) location.state.customPresets.splice(index, 1)
      }
      draftPresetIds.delete(presetId)
      throw new Error('ADD_FAILED')
    }
    return currentModeState.value.currentPreset
  }

  async function deleteCustomPreset(presetId: string): Promise<boolean> {
    const location = findCustomPresetLocation(presetId)
    if (!location) return false
    const { mode, state } = location
    const index = state.customPresets.findIndex(preset => preset.id === presetId)
    if (index < 0) return false

    const removedPreset = state.customPresets[index]
    const wasDraft = draftPresetIds.has(presetId)
    const previousCurrent = state.currentPreset
    const previousLast = state.lastPreset
    const previousPinned = state.pinnedPreset
    const previousAppliedMode = appliedMode.value
    const previousAppliedPresetId = appliedPresetId.value

    // 视觉上立即删除，不等待 deleteEqData。
    state.customPresets.splice(index, 1)
    draftPresetIds.delete(presetId)
    if (state.lastPreset === presetId) state.lastPreset = null
    if (state.pinnedPreset === presetId) state.pinnedPreset = null
    const fallback = state.systemPresets[0]?.id || ''
    if (state.currentPreset === presetId) state.currentPreset = fallback
    markMutation(mode)
    saveToStorage()
    if (!wasDraft && isLoggedIn()) {
      // 如果这是刚新增、服务器 list 尚未确认的标签，删除后必须同时撤销 pending add，
      // 否则下一次 list 合并会把用户刚删掉的标签重新补回来。
      clearPendingAdd(mode, presetId)
      addPendingDelete(mode, presetId)
    }

    // 如果当前硬件正在使用被删标签，立即切回该模式第一个系统预设；
    // 这属于设备/UI动作，不应阻塞标签消失。
    if (previousAppliedMode === mode && previousAppliedPresetId === presetId && fallback) {
      void applyPreset(mode, fallback, { markUserSelection: false })
    }

    if (wasDraft || !isLoggedIn()) return true

    try {
      const response = await removeEqPreset(mode, getUuid(), presetId, getLinktype())
      if (Number(response.code) === 1) {
        // 删除接口确认成功：把“已删除”的当前列表写成服务器确认快照。
        writeCloudModeSnapshot(mode, { protect: true, confirmActiveMode: true })
        scheduleRemoteReconcile(mode)
        return true
      }
    } catch (error) {
      console.error('[EQ] 删除预设失败:', error)
    }

    // 服务器明确失败才回滚，并撤销待确认删除标记。
    clearPendingDelete(mode, presetId)
    const safeIndex = Math.max(0, Math.min(index, state.customPresets.length))
    state.customPresets.splice(safeIndex, 0, removedPreset)
    state.currentPreset = previousCurrent
    state.lastPreset = previousLast
    state.pinnedPreset = previousPinned
    appliedMode.value = previousAppliedMode
    appliedPresetId.value = previousAppliedPresetId
    saveToStorage()
    return false
  }

  async function renameCustomPreset(presetId: string, name: string): Promise<boolean> {
    if (draftPresetIds.has(presetId)) {
      return finalizeCustomPresetDraft(presetId, name)
    }

    const location = findCustomPresetLocation(presetId)
    if (!location) return false
    const { mode, preset } = location
    const normalizedName = name.trim().slice(0, 20)
    if (!normalizedName) return false
    if (presetsForMode(mode).some(item => item.id !== presetId && item.name.toLowerCase() === normalizedName.toLowerCase())) {
      return false
    }
    markMutation(mode)
    if (isLoggedIn()) {
      try {
        const response = await renameEqPreset(mode, getUuid(), presetId, normalizedName, getLinktype())
        if (Number(response.code) !== 1) return false
      } catch (error) {
        console.error('[EQ] 重命名预设失败:', error)
        return false
      }
    }
    preset.name = normalizedName
    saveToStorage()
    if (isLoggedIn()) {
      writeCloudModeSnapshot(mode, { protect: true, confirmActiveMode: true })
      scheduleRemoteReconcile(mode)
    }
    return true
  }

  async function resetCurrentPreset(): Promise<boolean> {
    const preset = currentPreset.value
    if (!preset || preset.isCustom) return false
    const source = SYSTEM_PRESETS[activeMode.value].find(item => item.id === preset.id)
    if (!source) return false
    if (isLoggedIn()) {
      try {
        const response = await resetEqPreset(activeMode.value, getUuid(), preset.id, getLinktype())
        if (Number(response.code) !== 1) return false
      } catch (error) {
        console.error('[EQ] 重置预设失败:', error)
        return false
      }
    }
    preset.datapoint = clonePoints(source.datapoint)
    markMutation(activeMode.value)
    if (isLoggedIn()) {
      writeCloudModeSnapshot(activeMode.value, { protect: true, confirmActiveMode: true })
      scheduleRemoteReconcile(activeMode.value)
    }
    if (slidersInteractive.value) await applyCurrentEq()
    return true
  }

  async function pinPreset(presetId: string): Promise<boolean> {
    // UI 先立即排序；服务器失败才恢复。
    const mode = activeMode.value
    const state = getModeState(mode)
    if (!presetsForMode(mode).some(preset => preset.id === presetId)) return false

    const previousPinned = state.pinnedPreset
    state.pinnedPreset = presetId
    markMutation(mode)
    saveToStorage()

    if (isLoggedIn()) {
      try {
        const response = await pinEqPreset(mode, getUuid(), presetId, getLinktype())
        if (Number(response.code) !== 1) {
          state.pinnedPreset = previousPinned
          saveToStorage()
          return false
        }
        writeCloudModeSnapshot(mode, { protect: true, confirmActiveMode: true })
        scheduleRemoteReconcile(mode)
      } catch (error) {
        console.error('[EQ] 置顶预设失败:', error)
        state.pinnedPreset = previousPinned
        saveToStorage()
        return false
      }
    }

    return true
  }

  async function loadFromRemote(mode: EqMode, force = false): Promise<boolean> {
    if (!isLoggedIn() || (!force && remoteLoaded.value[mode])) return false

    const state = getModeState(mode)
    const requestGeneration = sourceGeneration.value
    const requestMutationVersion = mutationVersion.value[mode]
    const requestSelectionVersion = selectionVersion.value[mode]

    try {
      const response = await getEqList(mode, getUuid(), getLinktype())

      // 登录账号 / USB-2.4G 在请求期间发生变化：旧响应直接丢弃。
      if (requestGeneration !== sourceGeneration.value || !isLoggedIn()) return false
      if (Number(response.code) !== 1 || !response.data) return false

      const presets = response.data.presets.map(normalizePreset)
      const remoteSystem = presets.filter(preset => SYSTEM_PRESET_IDS[mode].includes(preset.id))
      const rawRemoteCustom = presets
        .filter(preset => !SYSTEM_PRESET_IDS[mode].includes(preset.id))
        .map((preset, index) => ({
          ...preset,
          isCustom: true,
          createdAt: preset.createdAt || Date.now() + index,
        }))

      // 删除接口返回成功后，服务器 list 可能还会短暂返回被删标签。
      // 只过滤当前账号/连接上下文中“用户明确删除且尚未被 list 确认消失”的 id。
      // 一旦 list 里真正看不到该 id，就清除 tombstone，后续完全恢复服务器原始数据。
      const pendingDeletes = activePendingDeleteIds(mode)
      const rawRemoteIds = new Set(rawRemoteCustom.map(preset => preset.id))
      let serverStillHasPendingDelete = false
      for (const presetId of pendingDeletes) {
        if (rawRemoteIds.has(presetId)) serverStillHasPendingDelete = true
        else clearPendingDelete(mode, presetId)
      }
      const activeDeletes = activePendingDeleteIds(mode)
      let remoteCustom = rawRemoteCustom.filter(preset => !activeDeletes.has(preset.id))

      // 新增写接口可能已经发出/成功，但 list 的最终一致性稍慢。
      // pending add 在这种窗口内继续显示，且会参与 nextCustomName，避免刷新后再次生成同名 Custom N。
      // 一旦服务器 list 真正包含同一个 presetId，立即清除 pending 标记。
      const rawRemoteIdsAfterDelete = new Set(remoteCustom.map(preset => preset.id))
      let serverStillMissingPendingAdd = false
      for (const entry of activePendingAdds(mode)) {
        const pendingPreset = entry.preset
        if (rawRemoteIdsAfterDelete.has(pendingPreset.id)) {
          clearPendingAdd(mode, pendingPreset.id)
          continue
        }
        if (activeDeletes.has(pendingPreset.id)) continue
        serverStillMissingPendingAdd = true
        remoteCustom.push({ ...normalizePreset(pendingPreset), isCustom: true, createdAt: pendingPreset.createdAt || Date.now() })
        rawRemoteIdsAfterDelete.add(pendingPreset.id)
      }

      // 请求发出后如果用户做了新增/删除/置顶/重命名等结构性操作，
      // 这次 list 很可能是操作前的旧快照，不能拿它把最新 UI 顶回去。
      if (mutationVersion.value[mode] !== requestMutationVersion) {
        remoteLoaded.value[mode] = false
        scheduleRemoteReconcile(mode)
        return false
      }

      // add/delete/pin/update 接口已经返回成功后，服务器 list 可能短时间仍是旧数据。
      // 当前缓存只保存“服务器已确认成功”的状态，所以保护窗口内若 list 与确认快照不一致，
      // 保留确认快照并继续后台重试；绝不让普通本地 UI 状态冒充服务器真值。
      const remoteFp = remoteFingerprint(
        remoteSystem.length ? remoteSystem : state.systemPresets,
        remoteCustom,
        response.data.lastPreset || null,
        response.data.defaultPreset || null,
      )
      const cachedFp = cloudModeFingerprint(mode)
      const guardUntil = Number(cloudGuardUntil.value[mode] || 0)
      if (cachedFp && Date.now() < guardUntil && cachedFp !== remoteFp) {
        remoteLoaded.value[mode] = false
        scheduleRemoteReconcile(mode, 700)
        return false
      }

      const preserveUserSelection = selectionVersion.value[mode] !== requestSelectionVersion
      const preservedCurrent = state.currentPreset
      const localDrafts = state.customPresets.filter(preset => draftPresetIds.has(preset.id))

      if (remoteSystem.length) state.systemPresets = remoteSystem
      state.customPresets = [...remoteCustom, ...localDrafts]
      state.lastPreset = response.data.lastPreset || null
      state.pinnedPreset = response.data.defaultPreset || null

      const allIds = new Set(presetsForMode(mode).map(preset => preset.id))
      if (preserveUserSelection && allIds.has(preservedCurrent)) {
        state.currentPreset = preservedCurrent
      } else if (state.lastPreset && allIds.has(state.lastPreset)) {
        state.currentPreset = state.lastPreset
      } else if (!allIds.has(state.currentPreset)) {
        state.currentPreset = state.systemPresets[0]?.id || state.customPresets[0]?.id || ''
      }

      // 登录态开关来自当前上下文第一次 0x80；标签/曲线刚由服务器校正后，
      // 如果该模式正处于应用态，只更新应用标签引用，不读取硬件 eqMode/eqTag。
      if (loggedInHardwareEnabledCaptured && appliedMode.value === mode && state.enabled) {
        appliedPresetId.value = state.currentPreset
      }

      cloudGuardUntil.value[mode] = 0
      remoteLoaded.value[mode] = true
      clearReconcileTimer(mode)
      // pending add 还没被 list 确认时，它只是“正在完成的写操作”，不能升级成服务器确认快照。
      // 等后续 list 真正包含该 id 后再写 cloud snapshot，保证登录态最终数据源仍然是服务器。
      if (!serverStillMissingPendingAdd) writeCloudModeSnapshot(mode)
      // 服务器仍返回某个已删除 id 时继续后台确认，但 UI 始终保持删除状态。
      if ((serverStillHasPendingDelete && activePendingDeleteIds(mode).size) || serverStillMissingPendingAdd) {
        scheduleRemoteReconcile(mode, 900)
      }
      return true
    } catch (error) {
      console.error(`[EQ] 加载 ${mode} 模式预设失败:`, error)
      return false
    }
  }

  async function loadAllFromRemote(force = false): Promise<void> {
    if (!isLoggedIn()) return
    await Promise.all([
      loadFromRemote('game', force),
      loadFromRemote('music', force),
      loadFromRemote('movie', force),
    ])
  }

  function syncHardwareStatus(enabled: boolean, modeValue: number, presetIndex: number): void {
    // 登录态：0x80 只在当前“账号 + 连接方式”上下文第一次回读时提供 EQ 开关。
    // 后续旧硬件 eqMode / eqTag / 曲线绝不覆盖服务器标签和 14 段数据。
    if (isLoggedIn()) {
      if (loggedInHardwareEnabledCaptured) return
      loggedInHardwareEnabledCaptured = true

      ;[gameEq.value, musicEq.value, movieEq.value].forEach(item => { item.enabled = false })
      if (!enabled) {
        appliedMode.value = null
        appliedPresetId.value = null
        return
      }

      const mode = activeMode.value
      const state = getModeState(mode)
      state.enabled = true
      appliedMode.value = mode
      appliedPresetId.value = state.currentPreset
      return
    }

    // 未登录：继续实时以硬件保存的 eqEnabled / eqMode / eqTag 为准。
    const mode = HARDWARE_TO_MODE[Math.max(0, Math.min(2, modeValue))] || 'game'
    const state = getModeState(mode)
    const preset = presetsForMode(mode)[Math.max(0, presetIndex)] || state.systemPresets[0]

    if (pendingHardwareWrite) {
      const expected = pendingHardwareWrite
      const confirmed = enabled === expected.enabled
        && (!enabled || (mode === expected.mode && preset.id === expected.presetId))

      if (confirmed) {
        pendingHardwareWrite = null
      } else if (Date.now() < expected.expiresAt) {
        return
      } else {
        pendingHardwareWrite = null
      }
    }

    ;[gameEq.value, musicEq.value, movieEq.value].forEach(item => { item.enabled = false })
    activeMode.value = mode
    hardwareStatusInitialized = true
    state.currentPreset = preset.id
    state.enabled = enabled
    appliedMode.value = enabled ? mode : null
    appliedPresetId.value = enabled ? preset.id : null
  }

  function syncLatestHardwareForCurrentContext(): void {
    const status = deviceStore.deviceStatus
    if (!status) return
    const useDongle = deviceStore.connectionType === '2.4G'
    const enabled = useDongle ? status.dongleEqEnabled : status.usbEqEnabled
    const mode = useDongle ? status.dongleEqMode : status.usbEqMode
    const tag = useDongle ? status.dongleEqTag : status.usbEqTag
    syncHardwareStatus(
      Number(enabled ?? status.eqEnabled ?? 0) === 1,
      Number(mode ?? status.eqMode ?? 0),
      Number(tag ?? status.eqTag ?? 0),
    )
  }

  function resetModeData(): void {
    gameEq.value = createModeState('game')
    musicEq.value = createModeState('music')
    movieEq.value = createModeState('movie')
    appliedMode.value = null
    appliedPresetId.value = null
    pendingHardwareWrite = null
    hardwareStatusInitialized = false
    draftPresetIds.clear()
    remoteLoaded.value = { game: false, music: false, movie: false }
    mutationVersion.value = { game: 0, music: 0, movie: 0 }
    selectionVersion.value = { game: 0, music: 0, movie: 0 }
    cloudGuardUntil.value = { game: 0, music: 0, movie: 0 }
    pendingDeleteIds.value = { game: {}, music: {}, movie: {} }
    pendingAdds.value = { game: {}, music: {}, movie: {} }
    ;(['game', 'music', 'movie'] as EqMode[]).forEach(clearReconcileTimer)
  }

  function handleSourceContextChange(): void {
    const connection = deviceStore.connectionType || hidManager.getConnectionType() || 'unknown'
    const nextKey = isLoggedIn()
      ? `user:${getUuid()}:${connection}`
      : `guest:${connection}`

    if (nextKey === sourceContextKey) return
    sourceContextKey = nextKey
    sourceGeneration.value += 1
    loggedInHardwareEnabledCaptured = false
    resetModeData()

    if (isLoggedIn()) {
      loadPendingDeletes()
      loadPendingAdds()
      // 这里只恢复“服务器确认快照”，用于避免刷新瞬间只看到系统默认值；
      // remoteLoaded 仍然是 false，随后一定重新请求服务器做最终校正。
      hydrateCloudSnapshot()
      // 待确认新增属于短期写操作状态：只在服务器 list 确认前补到页面，
      // 防止刷新后标签瞬间消失、再次新增产生同名记录。
      hydratePendingAddsIntoState()
      resumePendingDeletes()
      syncLatestHardwareForCurrentContext()
      void loadAllFromRemote(true)
    } else {
      // 退出登录后清掉云预设 UI，立刻恢复当前连接硬件保存的 EQ 状态。
      syncLatestHardwareForCurrentContext()
    }

    if (hidManager.isConnected()) void sendLoginStatus()
  }

  function loadFromStorage(): void {
    // 登录态只允许恢复“服务器确认快照”，不再从通用 eqSettings 读取任意本地标签/曲线。
    if (!isLoggedIn()) return
    hydrateCloudSnapshot()
  }

  function saveToStorage(): void {
    // 仅保留轻量 UI 会话信息，不把当前标签/曲线写成云端真值。
    // 服务器标签/曲线快照只会由 writeCloudModeSnapshot 在远端确认后写入。
    try {
      localStorage.setItem('eqSettings', JSON.stringify({ activeMode: activeMode.value }))
    } catch (error) {
      console.warn('[EQ] 保存 UI 会话状态失败:', error)
    }
  }

  watch(
    () => [
      userStore.isLoggedIn,
      userStore.uuid,
      deviceStore.connectionType,
    ] as const,
    () => {
      handleSourceContextChange()
    },
    { immediate: true },
  )


  return {
    gameEq,
    musicEq,
    movieEq,
    activeMode,
    appliedMode,
    appliedPresetId,
    remoteLoaded,
    currentModeState,
    currentSettings,
    allPresets,
    currentPreset,
    currentPresetData,
    isSystemPreset,
    canAddMoreCustom,
    isEnabled,
    currentMode,
    slidersInteractive,
    setActiveMode,
    switchMode,
    setEqEnabled,
    selectPreset,
    updateEqGain,
    updateEqFrequency,
    applyCurrentEq,
    createCustomPresetDraft,
    finalizeCustomPresetDraft,
    isDraftPreset,
    addCustomPreset,
    deleteCustomPreset,
    renameCustomPreset,
    resetCurrentPreset,
    pinPreset,
    loadFromRemote,
    loadAllFromRemote,
    syncHardwareStatus,
    loadFromStorage,
    saveToStorage,
  }
})
